import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaTimesCircle, 
  FaShieldAlt, 
  FaUndoAlt, 
  FaCalendarTimes, 
  FaRegClock, 
  FaBan, 
  FaExclamationTriangle, 
  FaEnvelope, 
  FaBuilding, 
  FaShareAlt,
  FaFileInvoice
} from 'react-icons/fa';
import NavbarComponent from './NavbarComponent';
import Footer from './Footer';
import './Policies.css';

const CancellationPolicy = ({ isLoggedIn, username, userRole, handleLogout }) => {
  return (
    <div className="policy-page-container">
      <NavbarComponent 
        isLoggedIn={isLoggedIn} 
        username={username} 
        userRole={userRole} 
        handleLogout={handleLogout} 
      />

      {/* Hero Section */}
      <section className="policy-hero">
        <div className="policy-badge">
          <FaTimesCircle /> Legal & Compliance
        </div>
        <h1>Cancellation Policy</h1>
        <p>
          Understand the terms, timelines, and procedures for canceling course enrollments, 
          paid workshop registrations, and platform subscriptions on CodingBoss.
        </p>
        <span className="policy-effective-date">Last Updated: September 2026</span>

        {/* Tab Navigation */}
        <div className="policy-nav-tabs">
          <Link to="/privacy-policy" className="policy-tab-link">
            <FaShieldAlt /> Privacy Policy
          </Link>
          <Link to="/cancellation-policy" className="policy-tab-link active">
            <FaTimesCircle /> Cancellation Policy
          </Link>
          <Link to="/refund-policy" className="policy-tab-link">
            <FaUndoAlt /> Refund Policy
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <main className="policy-main-content">
        <div className="policy-card">

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaFileInvoice /> 1. Overview
            </h2>
            <p className="policy-text">
              At <strong>CodingBoss</strong>, we strive to deliver high-quality technical education, live coding bootcamps, 
              proctored assessment tools, and personalized mentor support. We understand that circumstances may change, 
              and you may need to cancel your enrollment or subscription.
            </p>
            <p className="policy-text">
              This Cancellation Policy outlines the conditions under which cancellations are accepted and how you can 
              initiate a cancellation request.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaCalendarTimes /> 2. Live Batches & Bootcamp Courses
            </h2>
            <p className="policy-text">
              For structured cohort-based courses, masterclasses, and live training bootcamps:
            </p>
            <ul className="policy-list">
              <li>
                <strong>Cancellation Prior to Batch Start:</strong> You may request cancellation up to 
                <strong> 48 hours prior</strong> to the scheduled batch start date. A full cancellation will be processed, 
                subject to eligible refund terms.
              </li>
              <li>
                <strong>Cancellation After Batch Commencement:</strong> Once the first live class has commenced or more than 
                10% of course materials/recordings have been unlocked, cancellation requests will not be eligible for a refund, 
                though you may request a one-time <strong>Batch Transfer</strong> to a future cohort.
              </li>
              <li>
                <strong>Batch Transfer Requests:</strong> Eligible students can shift their enrollment to an upcoming batch 
                by notifying us at least 24 hours prior to class commencement, subject to seat availability.
              </li>
            </ul>

            <div className="policy-highlight-box info">
              <h4>Self-Paced Recorded Courses</h4>
              <p>
                For self-paced video courses, cancellation is permitted within <strong>24 hours of purchase</strong> provided 
                no certificate has been issued and less than 15% of the curriculum has been viewed or accessed.
              </p>
            </div>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaBan /> 3. Non-Cancellable Items & Services
            </h2>
            <p className="policy-text">The following products and services are non-cancellable once booked or consumed:</p>
            <ul className="policy-list">
              <li>
                <strong>Completed Assessments & Exams:</strong> Any test fees for proctored college exams or coding assessments that have already been attempted or completed.
              </li>
              <li>
                <strong>Issued Certificates:</strong> Digital verified completion certificates that have already been generated or verified.
              </li>
              <li>
                <strong>AI Mock Interviews & 1-on-1 Mentorship:</strong> Scheduled 1-on-1 mentor sessions canceled with less than 12 hours notice.
              </li>
              <li>
                <strong>Institutional & Corporate Training Contracts:</strong> Enterprise agreements are governed by the bespoke terms outlined in the institutional Memorandum of Understanding (MoU).
              </li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaExclamationTriangle /> 4. Cancellations Initiated by CodingBoss
            </h2>
            <p className="policy-text">
              CodingBoss reserves the right to postpone, reschedule, or cancel a workshop, contest, or live cohort due to:
            </p>
            <ul className="policy-list">
              <li>Unforeseen technical disruptions, server outages, or instructor emergencies.</li>
              <li>Failure to meet the minimum cohort enrollment threshold required for an interactive learning experience.</li>
              <li>Violation of our Student Code of Conduct, unauthorized sharing of credentials, or malicious attempts to reverse-engineer our platform.</li>
            </ul>
            <div className="policy-highlight-box success">
              <h4>Our Guarantee</h4>
              <p>
                In the rare event that CodingBoss cancels a course or event entirely, enrolled students will receive a 
                <strong> 100% full refund</strong> or an immediate complimentary pass to an alternative equivalent course.
              </p>
            </div>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaRegClock /> 5. Step-by-Step Cancellation Procedure
            </h2>
            <p className="policy-text">To cancel your enrollment, follow these simple steps:</p>
            <ol className="policy-list">
              <li>
                Send an email from your registered institutional/personal email to 
                <a href="mailto:info@codingboss.com" style={{ color: '#f59e0b', fontWeight: 600, marginLeft: '4px' }}>
                  info@codingboss.com
                </a>.
              </li>
              <li>Set the subject line to: <code>"Cancellation Request - [Your Full Name] - [Course/Transaction ID]"</code>.</li>
              <li>Include your registered email, contact number, course name, and the reason for cancellation in the email body.</li>
              <li>Our student support team will verify your eligibility and confirm your request within <strong>24 to 48 working hours</strong>.</li>
            </ol>
          </div>

          {/* Contact Details */}
          <div className="policy-contact-card">
            <h4>Need Help with a Cancellation?</h4>
            <p className="policy-text" style={{ marginBottom: 0 }}>
              Our dedicated student support team is available Monday through Saturday from 9:00 AM to 6:00 PM IST.
            </p>
            <div className="policy-contact-grid">
              <a href="mailto:info@codingboss.com" className="contact-info-item">
                <FaEnvelope /> info@codingboss.com
              </a>
              <div className="contact-info-item">
                <FaBuilding /> CodingBoss Support Desk
              </div>
              <Link to="/refund-policy" className="contact-info-item">
                <FaUndoAlt /> Review Refund Policy
              </Link>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CancellationPolicy;
